"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSessionIdWithDateString = exports.getGASessionDetails = exports.getGASessionTimestamp = exports.getGADeviceId = void 0;
/**
 * Extracts the Google Analytics device ID from the GA device ID cookie.
 * Cookie format expected: GA1.1.RANDOM_NUMBER.TIMESTAMP
 * See https://crossmasters.com/en/blog/ga-cookies-explained/
 * @param gaDeviceCookie GA cookie for `_ga` cookie key.
 * @returns The device ID string if found and valid, otherwise null.
 */
const getGADeviceId = (gaDeviceCookie) => {
    // Expected format: GA1.1.RANDOM_NUMBER.TIMESTAMP
    const parts = gaDeviceCookie.split('.');
    // If format is correct, return the device ID + cookie creation timestamp
    return parts.length === 4 ? `${parts[2]}.${parts[3]}` : null;
};
exports.getGADeviceId = getGADeviceId;
/**
 * Get the session timestamp from the GA session cookie.
 * See https://crossmasters.com/en/blog/ga-cookies-explained/
 * @param gaSessionCookie GA cookie for `_ga_<measurementId>` cookie key.
 * @returns The session timestamp or null if the value is not valid
 */
const getGASessionTimestamp = (gaSessionCookie) => {
    // Expected format: GS1.1.SESSION_ID.TIMESTAMP
    const mainParts = gaSessionCookie.split('.');
    let timestamp = null;
    if (/^GS1/i.test(gaSessionCookie)) {
        // GS1 Expected structure: GS1.1.SessionID.MoreStuff.etc...
        if (mainParts.length > 2) {
            timestamp = mainParts[2];
        }
    }
    else if (/^GS2/i.test(gaSessionCookie)) {
        // GS2 Expected structure: GS2.1.SessionID$MoreStuff$etc...
        if (mainParts.length > 2) {
            // Use the delimiter for version 2
            const subParts = mainParts[2].split('$');
            // SessionID is the first part after splitting by '$'
            if (subParts.length > 1) {
                // remove preceeding 's', it looks like this: s1313197301
                timestamp = subParts[0].slice(1);
            }
        }
    }
    const timestampNumber = parseInt(timestamp !== null && timestamp !== void 0 ? timestamp : '', 10);
    return !isNaN(timestampNumber) ? timestampNumber : null;
};
exports.getGASessionTimestamp = getGASessionTimestamp;
/**
 * Takes two cookies as a parameter and returns the device id, session timestamp. Those two values
 * are used to make the `sessionId` as used by the Analytics team. It's worth noting that we also will
 * need to concatenate the YYYYMMDD string with both these values to make a true `sessionId`.
 */
const getGASessionDetails = ({ gaDeviceCookie, gaSessionCookie }) => {
    const deviceId = (0, exports.getGADeviceId)(gaDeviceCookie);
    if (!deviceId) {
        return null;
    }
    const sessionTimestamp = (0, exports.getGASessionTimestamp)(gaSessionCookie);
    if (sessionTimestamp === null) {
        return null;
    }
    return {
        deviceId,
        sessionTimestamp,
    };
};
exports.getGASessionDetails = getGASessionDetails;
/**
 * Some scenarios (*cough cough* fastly *cough cough*) won't be able to use the
 * Intl.DateTimeFormat API, so we'll need a separate function to get that value
 */
const getSessionIdWithDateString = ({ gaDeviceCookie, gaSessionCookie }) => {
    const { deviceId, sessionTimestamp } = (0, exports.getGASessionDetails)({ gaDeviceCookie, gaSessionCookie }) || {};
    if (!deviceId || !sessionTimestamp) {
        return null;
    }
    // Get current date in Eastern Time (ET)
    const date = new Date();
    const options = {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    };
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(date);
    let dateString = '';
    for (const part of parts) {
        if (part.type === 'year') {
            dateString += part.value;
        }
        else if (part.type === 'month') {
            dateString += part.value;
        }
        else if (part.type === 'day') {
            dateString += part.value;
        }
    }
    return {
        deviceId,
        sessionTimestamp,
        dateString
    };
};
exports.getSessionIdWithDateString = getSessionIdWithDateString;
